'use client';

import React, { useState, useId } from 'react';
import { useLanguage } from '@/context/LanguageContext';

export default function RoiCalculator() {
  const { t } = useLanguage();
  const ticketInputId = useId();
  const clientsInputId = useId();

  // Inputs
  const [ticketPrice, setTicketPrice] = useState<number>(60);
  const [newClientsPerMonth, setNewClientsPerMonth] = useState<number>(5);
  const [selectedSolution, setSelectedSolution] = useState<'presencia' | 'sistema'>('presencia');

  // Solution costs
  const solutionCost = selectedSolution === 'presencia' ? 390 : 1500;
  const solutionName = selectedSolution === 'presencia' ? 'Onix Presència (390 €)' : 'Onix Sistema (1.500 €)';

  // Calculations
  const monthlyRevenue = ticketPrice * newClientsPerMonth;
  const clientsNeeded = Math.max(1, Math.ceil(solutionCost / (ticketPrice || 1)));
  const monthsToPayback = monthlyRevenue > 0 ? (solutionCost / monthlyRevenue).toFixed(1) : '—';
  const sixMonthsGross = monthlyRevenue * 6;
  const twelveMonthsGross = monthlyRevenue * 12;

  // WhatsApp prefilled message
  const waText = encodeURIComponent(
    `Hola! He utilitzat la calculadora de ROI per a ${solutionName}. Amb un tiquet mitjà de ${ticketPrice} € i uns ${newClientsPerMonth} clients/mes estimats, m'agradaria parlar del meu negoci.`
  );
  const waUrl = `https://wa.me/34694919757?text=${waText}`;

  return (
    <section
      id="calculadora-roi"
      className="py-24 px-6 bg-[#07070c] relative overflow-hidden border-t border-white/5"
      aria-label="Calculadora de Retorn de la Inversió (ROI)"
    >
      {/* Background radial aura */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[450px] bg-gradient-to-r from-gold/10 via-purple-brand/15 to-transparent blur-[160px] pointer-events-none" />

      <div className="max-w-5xl mx-auto space-y-12 relative z-10">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[11px] font-bold uppercase tracking-widest">
            {t.roiCalculator.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
            {t.roiCalculator.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            {t.roiCalculator.sub}
          </p>
        </div>

        {/* Interactive Calculator Container */}
        <div className="rounded-3xl p-6 sm:p-10 bg-gradient-to-b from-[#11111a] to-[#09090e] border border-gold/30 shadow-2xl shadow-gold/5 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Controls Column */}
          <div className="lg:col-span-7 space-y-7">
            {/* 1. Solution selector */}
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-wider text-slate-300 block">
                {t.roiCalculator.solutionLabel}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setSelectedSolution('presencia')}
                  className={`py-3 px-4 rounded-xl text-xs sm:text-sm font-extrabold transition-all border ${
                    selectedSolution === 'presencia'
                      ? 'bg-gold/15 text-gold border-gold shadow-md shadow-gold/10'
                      : 'bg-white/5 text-slate-400 border-white/5 hover:border-white/20'
                  }`}
                >
                  {t.roiCalculator.solutionP1}
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedSolution('sistema')}
                  className={`py-3 px-4 rounded-xl text-xs sm:text-sm font-extrabold transition-all border ${
                    selectedSolution === 'sistema'
                      ? 'bg-magenta-brand/15 text-magenta-brand border-magenta-brand shadow-md shadow-magenta-brand/10'
                      : 'bg-white/5 text-slate-400 border-white/5 hover:border-white/20'
                  }`}
                >
                  {t.roiCalculator.solutionP2}
                </button>
              </div>
            </div>

            {/* 2. Ticket Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label htmlFor={ticketInputId} className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  {t.roiCalculator.ticketLabel}
                </label>
                <span className="text-lg font-black text-gold bg-gold/10 px-3 py-0.5 rounded-lg border border-gold/20">
                  {ticketPrice} €
                </span>
              </div>
              <input
                id={ticketInputId}
                type="range"
                min="15"
                max="500"
                step="5"
                value={ticketPrice}
                onChange={(e) => setTicketPrice(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-gold"
              />
              <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                <span>15 €</span>
                <span>250 €</span>
                <span>500 €</span>
              </div>
            </div>

            {/* 3. New Clients Slider */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label htmlFor={clientsInputId} className="text-xs font-bold uppercase tracking-wider text-slate-300">
                  {t.roiCalculator.clientsLabel}
                </label>
                <span className="text-lg font-black text-emerald-400 bg-emerald-500/10 px-3 py-0.5 rounded-lg border border-emerald-500/20">
                  +{newClientsPerMonth} {t.roiCalculator.clientsPerMonth}
                </span>
              </div>
              <input
                id={clientsInputId}
                type="range"
                min="1"
                max="30"
                step="1"
                value={newClientsPerMonth}
                onChange={(e) => setNewClientsPerMonth(Number(e.target.value))}
                className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-emerald-400"
              />
              <div className="flex justify-between text-[10px] text-slate-500 font-mono">
                <span>1</span>
                <span>15</span>
                <span>30</span>
              </div>
            </div>
          </div>

          {/* Results Column */}
          <div className="lg:col-span-5 rounded-2xl bg-gradient-to-b from-[#181826] to-[#0f0f18] border border-gold/40 p-6 sm:p-7 space-y-6">
            <div className="border-b border-white/10 pb-4">
              <span className="text-[10px] font-black uppercase tracking-widest text-gold">
                {t.roiCalculator.resultTitle}
              </span>
              {/* Amortization highlight box */}
              <div className="mt-3 p-4 rounded-xl bg-gold/10 border border-gold/30 space-y-1">
                <span className="text-xs text-slate-300 block">
                  {t.roiCalculator.clientsNeededText}
                </span>
                <p className="text-3xl font-black text-white">
                  {t.roiCalculator.clientsNeededPrefix}
                  <span className="text-gold underline">{clientsNeeded} {t.roiCalculator.clientsNeededSuffix}</span>
                </p>
                <p className="text-[11px] text-slate-400">
                  {t.roiCalculator.clientsExplanation
                    .replace('{clients}', String(clientsNeeded))
                    .replace('{cost}', String(solutionCost))}
                </p>
              </div>
            </div>

            {/* Metrics breakdown */}
            <div className="space-y-3 text-xs sm:text-sm">
              <div className="flex items-center justify-between text-slate-300">
                <span>{t.roiCalculator.monthlyExtraText}</span>
                <span className="font-extrabold text-emerald-400 text-base">
                  +{monthlyRevenue.toLocaleString()} € / mes
                </span>
              </div>
              <div className="flex items-center justify-between text-slate-300">
                <span>{t.roiCalculator.paybackTimeText}</span>
                <span className="font-bold text-white">
                  ~{monthsToPayback} {Number(monthsToPayback) <= 1 ? t.roiCalculator.monthUnitSingular : t.roiCalculator.monthUnitPlural}
                </span>
              </div>
              <div className="flex items-center justify-between text-slate-300">
                <span>{t.roiCalculator.sixMonthsText}</span>
                <span className="font-bold text-white">
                  +{sixMonthsGross.toLocaleString()} €
                </span>
              </div>
              <div className="flex items-center justify-between text-slate-300">
                <span>{t.roiCalculator.twelveMonthsText}</span>
                <span className="font-bold text-gold">
                  +{twelveMonthsGross.toLocaleString()} €
                </span>
              </div>
            </div>

            {/* WhatsApp CTA */}
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-black text-xs uppercase tracking-wider shadow-lg shadow-emerald-900/30 transition-all text-center"
            >
              <span>💬 {t.roiCalculator.ctaWhatsApp}</span>
            </a>
          </div>
        </div>

        {/* Disclaimer */}
        <p className="text-center text-[11px] text-slate-400 max-w-2xl mx-auto leading-relaxed italic">
          {t.roiCalculator.disclaimer}
        </p>
      </div>
    </section>
  );
}
